import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

const ACCENTS = ['#ef4444', '#fbbf24', '#a78bfa', '#38bdf8', '#00d4aa', '#f472b6'];

export default function DebtPayoff({ defaultCollapsed, refreshKey, onRefresh }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', balance: '', interestRate: '', minimumPayment: '' });
  const [saving, setSaving] = useState(false);
  const [extraInput, setExtraInput] = useState(0);

  const load = useCallback(async () => {
    try {
      const d = await api.getDebts();
      setData(d);
      setExtraInput(d.extraPayment);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const saveDebt = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.balance || !form.minimumPayment) return;
    setSaving(true);
    try {
      await api.addDebt({
        name: form.name.trim(),
        balance: parseFloat(form.balance),
        interestRate: parseFloat(form.interestRate) || 0,
        minimumPayment: parseFloat(form.minimumPayment),
      });
      setForm({ name: '', balance: '', interestRate: '', minimumPayment: '' });
      setAdding(false);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const markPaidOff = async (id) => {
    try {
      await api.markDebtPaidOff(id);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteDebt = async (id) => {
    if (!window.confirm('Remove this debt?')) return;
    try {
      await api.deleteDebt(id);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  const saveExtra = async (amount) => {
    try {
      await api.updateDebtExtraPayment(amount);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  if (!data) return null;

  const totalMonthlyOutlay = data.totalMinimums + data.extraPayment;

  return (
    <CollapsiblePanel
      title="💳 Debt Freedom (Snowball)"
      defaultCollapsed={defaultCollapsed}
      headerExtra={
        <button style={styles.addBtn} onClick={(e) => { e.stopPropagation(); setAdding((a) => !a); }}>
          {adding ? 'Cancel' : '+ Add Debt'}
        </button>
      }
    >
      <p style={styles.helpText}>
        Pay minimums on everything, throw every extra dollar at your smallest
        balance first. When it's gone, that payment rolls into the next one -
        that's the snowball.
      </p>

      {adding && (
        <form onSubmit={saveDebt} style={styles.form}>
          <input
            style={styles.input}
            placeholder="Debt name (e.g. Credit Card)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div style={styles.formRow}>
            <input
              type="number" min="0" step="10"
              style={styles.input}
              placeholder="Balance $"
              value={form.balance}
              onChange={(e) => setForm({ ...form, balance: e.target.value })}
            />
            <input
              type="number" min="0" step="0.1"
              style={styles.input}
              placeholder="Interest rate % (optional)"
              value={form.interestRate}
              onChange={(e) => setForm({ ...form, interestRate: e.target.value })}
            />
            <input
              type="number" min="0" step="5"
              style={styles.input}
              placeholder="Min. payment $/mo"
              value={form.minimumPayment}
              onChange={(e) => setForm({ ...form, minimumPayment: e.target.value })}
            />
          </div>
          <button style={styles.saveBtn} type="submit" disabled={saving || !form.name.trim() || !form.balance || !form.minimumPayment}>
            {saving ? 'Saving...' : 'Add Debt'}
          </button>
        </form>
      )}

      {data.debts.length === 0 && !adding ? (
        <p style={styles.empty}>No debts tracked - add one above to build your payoff plan.</p>
      ) : data.debts.length > 0 && (
        <>
          <div style={styles.extraBox}>
            <div style={styles.extraRow}>
              <span style={styles.extraLabel}>Extra payment toward debt each month</span>
              <div style={styles.extraInputWrap}>
                <span style={styles.dollarSign}>$</span>
                <input
                  type="number" min="0" step="10"
                  style={styles.extraInput}
                  value={extraInput}
                  onChange={(e) => setExtraInput(e.target.value)}
                  onBlur={() => saveExtra(parseFloat(extraInput) || 0)}
                />
              </div>
            </div>
            <p style={styles.extraNote}>
              Total monthly outlay toward debt: <strong>{fmt.currency(totalMonthlyOutlay)}</strong>
              {' '}({fmt.currency(data.totalMinimums)} minimums + {fmt.currency(data.extraPayment)} extra)
            </p>
          </div>

          {data.monthsToFreedom != null ? (
            <div style={styles.summaryBox}>
              <div style={styles.summaryStat}>
                <span style={styles.summaryValue}>{data.monthsToFreedom}</span>
                <span style={styles.summaryLabel}>months to debt-free</span>
              </div>
              <div style={styles.summaryStat}>
                <span style={styles.summaryValue}>
                  {new Date(data.debtFreeDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
                <span style={styles.summaryLabel}>freedom date</span>
              </div>
              <div style={styles.summaryStat}>
                <span style={{ ...styles.summaryValue, color: '#fbbf24' }}>{fmt.currency(data.totalInterestPaid)}</span>
                <span style={styles.summaryLabel}>total interest paid</span>
              </div>
            </div>
          ) : (
            <div style={styles.warningBox}>
              At this payment level, one or more debts may never be paid off -
              interest is outpacing what's being paid down. Consider increasing
              minimums or the extra payment.
            </div>
          )}

          <div style={styles.timeline}>
            {data.order.map((debt, i) => {
              const accent = ACCENTS[i % ACCENTS.length];
              const years = debt.paidOffMonth ? (debt.paidOffMonth / 12).toFixed(1) : null;
              return (
                <div key={debt.id} style={styles.debtCard}>
                  <div style={styles.debtCardTop}>
                    <div style={styles.debtOrderBadge(accent)}>{i + 1}</div>
                    <div style={styles.debtInfo}>
                      <div style={styles.debtName}>{debt.name}</div>
                      <div style={styles.debtMeta}>
                        {fmt.currency(debt.startingBalance)} · {debt.interestRate}% APR · {fmt.currency(debt.minimumPayment)}/mo min
                      </div>
                    </div>
                    <div style={styles.debtActions}>
                      <button style={styles.paidOffBtn} onClick={() => markPaidOff(debt.id)} title="Mark as paid off">✓</button>
                      <button style={styles.deleteBtn} onClick={() => deleteDebt(debt.id)} title="Remove">✕</button>
                    </div>
                  </div>
                  <div style={styles.debtPayoffLine}>
                    {debt.paidOffMonth
                      ? <span style={{ color: accent, fontWeight: '700' }}>Paid off month {debt.paidOffMonth} (~{years} yrs)</span>
                      : <span style={{ color: '#ef4444' }}>Never paid off at current pace</span>}
                    {debt.interestPaid > 0 && (
                      <span style={styles.debtInterestNote}> · {fmt.currency(debt.interestPaid)} interest</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </CollapsiblePanel>
  );
}

const styles = {
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  addBtn: {
    background: 'transparent',
    border: '1px solid #ef4444',
    color: '#ef4444',
    borderRadius: '999px',
    padding: '4px 12px',
    fontSize: '0.72rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  form: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  formRow: { display: 'flex', gap: '0.6rem' },
  input: {
    flex: 1,
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    color: '#fff',
    fontSize: '0.82rem',
    fontFamily: "'Syne', sans-serif",
    boxSizing: 'border-box',
    minWidth: 0,
  },
  saveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.65rem',
    fontSize: '0.82rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' },
  extraBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.9rem 1rem',
    marginBottom: '1rem',
  },
  extraRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '0.5rem',
  },
  extraLabel: {
    color: '#e5e7eb',
    fontSize: '0.8rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  extraInputWrap: { position: 'relative', width: '110px', flexShrink: 0 },
  dollarSign: {
    position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
    color: '#6b7280', fontSize: '0.8rem',
  },
  extraInput: {
    width: '100%',
    boxSizing: 'border-box',
    background: '#111827',
    border: '1px solid #00d4aa',
    borderRadius: '8px',
    padding: '8px 10px 8px 22px',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'DM Mono', monospace",
  },
  extraNote: {
    color: '#6b7280',
    fontSize: '0.72rem',
    lineHeight: '1.5',
    margin: 0,
  },
  summaryBox: {
    display: 'flex',
    justifyContent: 'space-around',
    background: 'linear-gradient(135deg, rgba(0,212,170,0.1), rgba(0,212,170,0.02))',
    border: '1px solid rgba(0,212,170,0.25)',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1.25rem',
  },
  summaryStat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
  summaryValue: {
    color: '#fff',
    fontSize: '1.3rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '0.65rem',
    fontFamily: "'Syne', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  warningBox: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
    color: '#ef4444',
    fontSize: '0.78rem',
    lineHeight: '1.5',
    marginBottom: '1.25rem',
  },
  timeline: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  debtCard: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
  },
  debtCardTop: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  debtOrderBadge: (color) => ({
    width: '26px', height: '26px', borderRadius: '50%',
    background: `${color}22`, border: `1px solid ${color}`, color,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.75rem', fontWeight: '800', flexShrink: 0,
    fontFamily: "'DM Mono', monospace",
  }),
  debtInfo: { flex: 1, minWidth: 0 },
  debtName: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  debtMeta: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  debtActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  paidOffBtn: {
    background: 'transparent',
    border: '1px solid #00d4aa',
    color: '#00d4aa',
    borderRadius: '6px',
    width: '26px', height: '26px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '700',
  },
  deleteBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '6px',
    width: '26px', height: '26px',
    cursor: 'pointer',
    fontSize: '0.7rem',
  },
  debtPayoffLine: {
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid #1e2a3a',
    fontSize: '0.72rem',
    fontFamily: "'DM Mono', monospace",
  },
  debtInterestNote: { color: '#6b7280' },
};
